/**
 * rother3 companion — writing a ZIP, which is what an .xlsx file is.
 *
 * ## Why this exists
 *
 * The workspace can hand its reader one file containing every business they imported, and Excel
 * is the format they will actually open it in. `.xlsx` is not a spreadsheet format — it is a ZIP
 * of XML parts, and Excel refuses the file if any of that is malformed.
 *
 * ## Why no library
 *
 * Same reasoning as `zip.js`, which reads the same container format in ~150 lines with
 * `DecompressionStream`. The extra step here is that writing needs no compressor at all: the
 * ZIP format allows **stored** (uncompressed) entries, and Excel, LibreOffice, Numbers and
 * Google Sheets all read them. So this is a directory-of-files serialiser, not a deflater, and
 * the only real work is the CRC-32 that every entry carries.
 *
 * Vendoring `fflate`/`jszip` instead would add a supply-chain surface and tens of KB to a
 * project whose stated constraints include "no budget" and "reproducible", to avoid ~90 lines
 * that call nothing outside this file.
 *
 * ## Why stored entries are acceptable here
 *
 * An uncompressed .xlsx for 100 businesses is roughly 60–120 KB, against a few KB compressed.
 * That is the entire cost, paid once, on a file the user saves and opens locally. For that
 * trade, "no compression code to get wrong" is the better deal — a subtly broken deflate stream
 * produces a file Excel refuses with no useful message, while a wrong CRC produces the same
 * failure from a much smaller and more auditable function.
 *
 * ## Scope
 *
 * Writes. Never reads, never extracts, never touches the filesystem. Names are validated as
 * relative paths with no `..`, because an .xlsx part name is a path inside the archive and a
 * writer that emits `../../evil.xml` is a bug waiting for someone to notice it late.
 */

/** Local file header signature. `PK\x03\x04`. */
const LOCAL_SIG = 0x04034b50;
/** Central directory header signature. `PK\x01\x02`. */
const CENTRAL_SIG = 0x02014b50;
/** End-of-central-directory signature. `PK\x05\x06`. */
const EOCD_SIG = 0x06054b50;

/** Method 0 — stored. The only method this writer emits, and the only one it needs. */
const METHOD_STORE = 0;

/**
 * Bit 11 of the general-purpose flag: file name and comment are UTF-8.
 *
 * Set unconditionally. Every name this module is given is ASCII today, but a future sheet
 * title could carry an em dash or a non-Latin name, and a ZIP that misdeclares its encoding
 * produces mojibake in the one place a user is guaranteed to look — the file they just opened.
 */
const FLAG_UTF8 = 0x0800;

/**
 * Convert a JS date to the MS-DOS date/time pair ZIP headers use.
 *
 * DOS timestamps are local time with no timezone, stored in a packed 16/16-bit pair. The year
 * field starts at 1980 and cannot express anything later than 2107, which is the same range
 * the format itself has always had.
 *
 * @param {Date} date
 * @returns {{time: number, date: number}}
 */
function dosTimestamp(date) {
  const year = date.getFullYear();
  // Below 1980 the 7-bit year field cannot hold it. Clamping to 1980-01-01 is what the format
  // itself does — the alternative is wrapping into 2104, which sorts as newer than everything.
  const safeYear = year < 1980 ? 1980 : year > 2107 ? 2107 : year;
  const time =
    (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2);
  const packedDate = ((safeYear - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();
  return { time: time & 0xffff, date: packedDate & 0xffff };
}

/**
 * CRC-32 (IEEE 802.3), the checksum every ZIP entry carries.
 *
 * Built from a lazily-created table rather than `crc32`/`crc-table` packages: the polynomial
 * 0xEDB88320 is the reflected form of the standard one, and the table is 256 entries of
 * `Uint32Array` rather than anything large enough to matter.
 */
let CRC_TABLE = null;

/** @returns {Uint32Array} */
function crcTable() {
  if (CRC_TABLE) {return CRC_TABLE;}
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      // Reflected algorithm: shift right, XOR the polynomial on a set low bit.
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  CRC_TABLE = table;
  return table;
}

/**
 * @param {Uint8Array} bytes
 * @returns {number} unsigned 32-bit CRC
 */
export function crc32(bytes) {
  const table = crcTable();
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    c = table[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

/** @param {string} s @returns {Uint8Array} */
function utf8(s) {
  return new TextEncoder().encode(s);
}

/**
 * Reject an entry name that is not a safe relative path.
 *
 * An .xlsx part name is a path inside the archive. A leading `/`, a `..` segment or a Windows
 * drive letter would place the part outside the archive root, and Office's own repair logic has
 * historically been uneven about paths like that. Nothing this module writes legitimately needs
 * one, so all three are refused loudly.
 *
 * @param {string} name
 * @returns {string} the name, unchanged
 */
function assertSafeName(name) {
  if (typeof name !== 'string' || name.length === 0) {
    throw new Error('zip: an entry needs a non-empty name');
  }
  if (name.startsWith('/') || name.includes('\\') || /^[a-zA-Z]:/.test(name)) {
    throw new Error(`zip: entry name must be a plain relative path, got "${name}"`);
  }
  if (name.split('/').some((seg) => seg === '..' || seg === '.')) {
    throw new Error(`zip: entry name must not contain "." or ".." segments, got "${name}"`);
  }
  return name;
}

/**
 * Build a ZIP archive from in-memory files.
 *
 * ## Why the layout is written by hand
 *
 * A ZIP is three structures in sequence: a local header + data per entry, one central directory
 * record per entry pointing back at its local header, and an end-of-central-directory record
 * giving the directory's offset and size. The local headers are written first because a reader
 * streams them, but the central directory is what actually makes the archive seekable — the
 * local headers alone are not a valid ZIP.
 *
 * ## Size limits, and why there are none
 *
 * Fields are 32-bit, so an archive over 4 GB cannot be described. An .xlsx here holds one row
 * per business from a browser tab, and passing 4 GB of that is not a realistic path to a
 * failure, so this writer does not pretend to handle ZIP64. If it ever has to, the honest
 * outcome is a wrapped value and a corrupt file — so the entry count is checked instead, and
 * the real guard is that this is only ever fed one comparison table.
 *
 * @param {{name: string, data: Uint8Array}[]} entries
 * @param {{date?: Date}} [options]
 * @returns {Uint8Array} the archive
 */
export function createZip(entries, options = {}) {
  const list = Array.isArray(entries) ? entries : [];
  if (list.length > 65535) {
    throw new Error(`zip: ${list.length} entries exceeds the 65535 this writer supports`);
  }

  const stamp = dosTimestamp(options.date instanceof Date ? options.date : new Date());
  const now = stamp.time;
  const today = stamp.date;

  /** @type {Uint8Array[]} */
  const parts = [];
  /** @type {{name: Uint8Array, crc: number, size: number, offset: number}[]} */
  const central = [];
  let offset = 0;

  const push = (bytes) => {
    parts.push(bytes);
    offset += bytes.length;
  };

  for (const entry of list) {
    const nameBytes = utf8(assertSafeName(entry.name));
    const data = entry.data instanceof Uint8Array ? entry.data : new Uint8Array(entry.data);
    const crc = crc32(data);
    const localOffset = offset;

    // Local file header: 30 bytes, then the name, then the data with no descriptor.
    const header = new Uint8Array(30 + nameBytes.length);
    const view = new DataView(header.buffer);
    view.setUint32(0, LOCAL_SIG, true);
    view.setUint16(4, 20, true); // Version needed: 2.0, the floor for stored entries.
    view.setUint16(6, FLAG_UTF8, true);
    view.setUint16(8, METHOD_STORE, true);
    view.setUint16(10, now, true);
    view.setUint16(12, today, true);
    view.setUint32(14, crc, true);
    view.setUint32(18, data.length, true); // Compressed size == uncompressed, stored.
    view.setUint32(22, data.length, true);
    view.setUint16(26, nameBytes.length, true);
    view.setUint16(28, 0, true); // Extra field length.
    header.set(nameBytes, 30);

    push(header);
    push(data);
    central.push({ name: nameBytes, crc, size: data.length, offset: localOffset });
  }

  const centralStart = offset;

  for (const item of central) {
    // Central directory header: 46 bytes, then the name.
    const header = new Uint8Array(46 + item.name.length);
    const view = new DataView(header.buffer);
    view.setUint32(0, CENTRAL_SIG, true);
    view.setUint16(4, 20, true); // Version made by.
    view.setUint16(6, 20, true); // Version needed.
    view.setUint16(8, FLAG_UTF8, true);
    view.setUint16(10, METHOD_STORE, true);
    view.setUint16(12, now, true);
    view.setUint16(14, today, true);
    view.setUint32(16, item.crc, true);
    view.setUint32(20, item.size, true);
    view.setUint32(24, item.size, true);
    view.setUint16(28, item.name.length, true);
    view.setUint16(30, 0, true); // Extra.
    view.setUint16(32, 0, true); // Comment.
    view.setUint16(34, 0, true); // Disk number start.
    view.setUint16(36, 0, true); // Internal attributes.
    view.setUint32(38, 0, true); // External attributes.
    view.setUint32(42, item.offset, true);
    header.set(item.name, 46);
    push(header);
  }

  const centralSize = offset - centralStart;

  // End of central directory: 22 bytes, fixed, with no comment.
  const eocd = new Uint8Array(22);
  const eocdView = new DataView(eocd.buffer);
  eocdView.setUint32(0, EOCD_SIG, true);
  eocdView.setUint16(4, 0, true); // This disk.
  eocdView.setUint16(6, 0, true); // Disk with the central directory.
  eocdView.setUint16(8, central.length, true);
  eocdView.setUint16(10, central.length, true);
  eocdView.setUint32(12, centralSize, true);
  eocdView.setUint32(16, centralStart, true);
  eocdView.setUint16(20, 0, true); // Comment length.
  push(eocd);

  // Concatenate. `parts` is small (a handful of XML parts), so a single pass beats tracking
  // offsets into a growing buffer.
  const total = parts.reduce((sum, p) => sum + p.length, 0);
  const out = new Uint8Array(total);
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}